import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, Youtube } from "lucide-react";

const TRUST_LOGOS = [
  "https://cdn.simpleicons.org/airbnb/FAF9F6",
  "https://cdn.simpleicons.org/stripe/FAF9F6",
  "https://cdn.simpleicons.org/youtube/FAF9F6",
  "https://cdn.simpleicons.org/tiktok/FAF9F6",
  "https://cdn.simpleicons.org/instagram/FAF9F6",
];

const TESTIMONIALS = [
  {
    quote: "I went from zero online presence to getting direct calls from customers every week. Their online promotion strategy just works.",
    author: "Vinay Gupta",
    role: "Navya Enterprises, Udaipur",
    label: "Online Reach Increase",
  },
  {
    quote: "Mint Muse transformed our villa from a 40% occupancy to consistently booked out.",
    author: "Priya Sharma",
    role: "Villa Owner, Jaipur",
    label: "Occupancy Increase",
  },
];

const HERO_BG = "https://images.pexels.com/photos/31032753/pexels-photo-31032753.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function HomePage() {
  return (
    <div>
      {/* ========== HERO SECTION (DARK) ========== */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center bg-dark_base overflow-hidden"
      >
        {/* Background Image Overlay */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-dark_base/80 via-dark_base/60 to-dark_base" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
          <motion.div
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            {/* Label */}
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-6 font-body"
            >
              Digital Growth Agency
            </motion.p>

            {/* Headline */}
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="font-heading text-5xl md:text-6xl lg:text-[5.5rem] leading-[1.05] tracking-tight font-extrabold text-cream mb-8"
            >
              Where High-End{" "}
              <span className="font-accent italic font-normal text-mint">Vision</span>{" "}
              Meets High-Yield{" "}
              <span className="font-accent italic font-normal text-mint">Growth.</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={fadeUp}
              custom={2}
              className="text-lg md:text-xl leading-relaxed text-cream/70 max-w-2xl font-body"
            >
              Mint Muse Digital transforms online presence into measurable profit by driving organic reach and customer conversion.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ========== CONTENT TRANSITION (LIGHT) ========== */}
      <section className="bg-light_base -mt-12 rounded-t-[3rem] relative z-10">
        {/* What We Do */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" id="about">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16"
          >
            <motion.div variants={fadeUp} custom={0} className="md:col-span-5">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body">
                What We Do
              </p>
              <h2 className="font-heading text-4xl md:text-5xl leading-tight tracking-tight font-bold text-dark_base">
                Tailored Strategies for{" "}
                <span className="font-accent italic font-normal text-mint">Modern Assets.</span>
              </h2>
            </motion.div>

            <motion.div variants={fadeUp} custom={1} className="md:col-span-7 space-y-6">
              <p className="text-base md:text-lg leading-relaxed text-dark_base/70 font-body">
                We don't just create content; we create systems. Choose your path to a more profitable digital presence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to="/services"
                  data-testid="about-cta-services"
                  className="bg-transparent border border-dark_base/20 text-dark_base py-3 px-6 rounded-full hover:bg-dark_base hover:text-light_base transition-all duration-300 text-center font-body font-bold text-sm"
                >
                  Unfold Your Potential
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* ========== TESTIMONIALS ========== */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body"
            >
              Client Results
            </motion.p>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="font-heading text-4xl md:text-5xl leading-tight tracking-tight font-bold text-dark_base mb-16"
            >
              Real <span className="font-accent italic font-normal text-mint">ROI</span>,
              Real Stories
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl" data-testid="testimonials-section">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                data-testid={`testimonial-card-${i}`}
                className="bg-dark_surface rounded-3xl p-8 md:p-10 flex flex-col justify-between group hover:scale-[1.02] transition-transform duration-500"
              >
                <div>
                  <p className="text-xs tracking-[0.15em] uppercase font-bold text-mint mb-6 font-body">{t.label}</p>
                  <p className="font-body text-cream/80 text-sm leading-relaxed mb-8">"{t.quote}"</p>
                </div>
                <div>
                  <p className="font-body text-cream font-semibold text-sm">{t.author}</p>
                  <p className="font-body text-cream/40 text-xs">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Social CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <p className="font-body text-dark_base/60 text-sm">Check out more of our work on</p>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/mintmusedigital/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="testimonial-instagram-link"
                className="flex items-center gap-2 bg-dark_base text-cream py-2.5 px-5 rounded-full hover:bg-mint hover:text-dark_base transition-all duration-300 text-sm font-body font-semibold"
              >
                <Instagram size={16} /> Instagram
              </a>
              <a
                href="https://www.youtube.com/@MintMuseDigital"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="testimonial-youtube-link"
                className="flex items-center gap-2 bg-dark_base text-cream py-2.5 px-5 rounded-full hover:bg-mint hover:text-dark_base transition-all duration-300 text-sm font-body font-semibold"
              >
                <Youtube size={16} /> YouTube
              </a>
            </div>
          </motion.div>
        </div>

        {/* ========== CTA BANNER ========== */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-dark_base rounded-3xl p-12 md:p-20 text-center"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body">
              Ready to grow?
            </p>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight tracking-tight font-bold text-cream mb-8">
              Let's Build Something{" "}
              <span className="font-accent italic font-normal text-mint">Exceptional</span>
            </h2>
            <Link
              to="/contact"
              data-testid="home-bottom-cta"
              className="inline-flex bg-mint text-dark_base font-bold py-4 px-10 rounded-full hover:bg-mint-hover transition-colors duration-300 font-body"
            >
              Start a Conversation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
